import LWElement from './../../lib/lw-element.js';
import ast from './ast.js';
import attachments from '../../lib/ticket-attachments.js';

// The ticket conversation page: the chat thread with system notices and
// feedback (solved prompt, stars, reply thumbs), the reply compose with
// attachments, and the close-ticket dialog. First page extracted from
// web-dashboard; the ticket LISTS and the new-ticket dialog stay on the host.
//
// Contract with the host (web-dashboard):
//  - the host renders <web-ticket-view lw-if="page === 'ticket-view'"> and
//    reaches this component through host._ticketView (registered below), for
//    routing (applyRoute -> viewTicket), breadcrumbs (ticketDetail.subject)
//    and session teardown (reset());
//  - routing state (page, ticketViewSource) and the viewer's identity stay
//    host-owned; the accessors below delegate so the page code reads and
//    writes them as its own;
//  - host services (apiFetch, toasts, confirm dialog, formatters) are
//    one-line delegates.
class WebTicketView extends LWElement {
  constructor() {
    super(ast);
    // Register with the host NOW, while this element still sits in the
    // host's template tree: the host's very first update parks this
    // component (its page lw-if is false on login), and a parked element
    // has no parent chain left to find the host through — a deferred
    // registration would silently miss and kill ticket navigation.
    // The reference is CACHED because most of this component's work starts
    // while it is parked (viewTicket fetches first, then flips the page) —
    // and a parked element has no parent chain to walk.
    this._host = this._findHost();
    if (this._host) this._host._ticketView = this;
  }

    // The template root is lw-if="ticketDetail": during construction (before
    // class fields initialize) it stays parked, so expressions never see the
    // fields half-made.
    ticketDetail = {};
    ticketMessages = [];
    ticketReplyBody = '';
    ticketReplyLoading = false;
    ticketAiLoading = false;
    _ticketViewSeq = 0;
    // Close-dialog state (an owner close bundles feedback with the close).
    closeComment = '';
    closeSolved = null;
    closeRating = 0;
    closeTicketLoading = false;
    starValues = [1, 2, 3, 4, 5];
    // Compose-area drag state and picked reply files (the attachments mixin
    // operates on these; kind 'reply' only — 'new' lives on the host).
    dragTarget = '';
    replyFiles = [];
    _attachmentUrls = {};
    _attachmentTicket = '';

    // Nearest enclosing leanweb component (the dashboard), cached at
    // construction so it stays reachable while this component is parked.
    host() {
      return this._host ?? (this._host = this._findHost());
    }

    _findHost() {
      let el = this.parentElement;
      while (el && !el.localName.startsWith(leanweb.componentPrefix)) el = el.parentElement;
      return el;
    }

    // --- host-owned state, delegated -------------------------------------
    // (getters are skipped by LWElement's method binding, so these stay live)

    get page() {
      return this.host()?.page;
    }
    set page(v) {
      const h = this.host();
      if (h && h.page !== v) {
        h.page = v;
        h.update();
      }
    }

    get ticketViewSource() {
      return this.host()?.ticketViewSource ?? '';
    }
    set ticketViewSource(v) {
      const h = this.host();
      if (h) h.ticketViewSource = v;
    }

    get isAdmin() {
      return this.host()?.isAdmin ?? false;
    }
    get userName() {
      return this.host()?.userName ?? '';
    }
    get userDisplayName() {
      return this.host()?.userDisplayName ?? '';
    }

    // --- host services ----------------------------------------------------

    apiFetch(...a) {
      return this.host().apiFetch(...a);
    }
    apiUrl(...a) {
      return this.host().apiUrl(...a);
    }
    showToast(...a) {
      return this.host()?.showToast(...a);
    }
    showConfirm(...a) {
      return this.host()?.showConfirm(...a);
    }
    formatDateTime(...a) {
      return this.host()?.formatDateTime(...a);
    }
    formatFileSize(...a) {
      return this.host()?.formatFileSize(...a);
    }
    userProfileHref(...a) {
      return this.host()?.userProfileHref(...a);
    }
    loadTicketUnreadCounts(...a) {
      return this.host()?.loadTicketUnreadCounts(...a);
    }

    // Session teardown (account switch/logout): the host calls this through
    // its direct reference, so it works even while this component is parked.
    reset() {
      this.ticketDetail = {};
      this.ticketMessages = [];
      this.ticketReplyBody = '';
      this._revokePicked(this.replyFiles);
      this.replyFiles = [];
      this.revokeAttachmentUrls();
      this._attachmentTicket = '';
      this.closeComment = '';
      this.closeSolved = null;
      this.closeRating = 0;
      this.update();
    }

    // --- the page ----------------------------------------------------------

    isSentMessage(m) {
      if (this.ticketViewSource === 'admin') {
        return !!m.is_admin;
      }
      return !m.is_admin;
    }

    // Author label: display name, else "First L." from the profile name,
    // else the email (new accounts carry the email in both name fields).
    chatName(m) {
      const email = m.user_email || '';
      const display = (m.user_display_name || '').trim();
      if (display && display.toLowerCase() !== email.toLowerCase()) return display;
      const name = (m.user_name || '').trim();
      if (!name || name.toLowerCase() === email.toLowerCase()) return email;
      const parts = name.split(/\s+/);
      if (parts.length === 1) return parts[0];
      return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
    }

    chatInitial(m) {
      return this.chatName(m).charAt(0).toUpperCase();
    }

    // Friendly salutation name: display name, else first name, else '' when we
    // only have an email. (chatName renders "First L." for the message header;
    // a "Dear ___," greeting reads better with just the first name.)
    greetingName(m) {
      const email = m.user_email || '';
      const display = (m.user_display_name || '').trim();
      if (display && display.toLowerCase() !== email.toLowerCase()) return display;
      const name = (m.user_name || '').trim();
      if (name && name.toLowerCase() !== email.toLowerCase()) return name.split(/\s+/)[0];
      return '';
    }

    // Pre-filled reply body. Admins answering a ticket get a greeting/sign-off
    // template addressed to the ticket owner and signed with the admin's name;
    // users always start from an empty box.
    defaultReplyBody() {
      if (this.ticketViewSource !== 'admin' || !this.isAdmin) return '';
      const owner = (this.ticketMessages || []).find(m => m.user_id === this.ticketDetail.user_id)
        || (this.ticketMessages || [])[0];
      const userName = owner ? this.greetingName(owner) : '';
      const adminName = (this.userDisplayName || this.userName || '').trim();
      const greeting = userName ? 'Dear ' + userName + ',' : 'Hello,';
      return greeting + '\n\n\nThanks,\n' + adminName;
    }

    // Read receipt shown under the viewer's own (sent) messages: whether the
    // other party has read it, and when.
    messageReadLabel(m) {
      return m.is_read ? 'Read · ' + this.formatDateTime(m.read_at) : 'Sent';
    }

    // Receipts mark boundaries, not every bubble: the read state derives from
    // a watermark, so one "Read · time" under the viewer's last read sent
    // message implies everything before it was read — and one trailing "Sent"
    // under the newest sent message says the messages after the frontier
    // haven't been seen yet. Both collapse into a single receipt when the
    // last message is the read frontier (or nothing is read yet).
    receiptIds() {
      let lastRead = null, lastSent = null, lastSentRead = false;
      for (const m of (this.ticketMessages || [])) {
        if (m.kind !== 'message' || !this.isSentMessage(m)) continue;
        lastSent = m.id;
        lastSentRead = !!m.is_read;
        if (m.is_read) lastRead = m.id;
      }
      const ids = [];
      if (lastRead) ids.push(lastRead);
      if (lastSent && !lastSentRead) ids.push(lastSent);
      return ids;
    }

    showReceipt(m) {
      return this.receiptIds().includes(m.id);
    }

    // Close/reopen rows are system notices, not chat bubbles. The "closed by"
    // line renders from kind + author, so names stay live and the row's body
    // stays free for the owner's closing comment.
    isSystemMessage(m) {
      return m.kind === 'close' || m.kind === 'reopen';
    }

    systemMessageText(m) {
      const action = m.kind === 'close' ? 'closed' : 'reopened';
      const who = this.chatName(m) || (m.is_admin ? 'Support' : 'Customer');
      return 'Ticket ' + action + ' by ' + who;
    }

    // The latest close notice carries the ticket feedback (prompt or result);
    // earlier closes of a reopened ticket stay plain history.
    isActiveCloseMessage(m) {
      if (m.kind !== 'close' || this.ticketDetail.status !== 'closed') return false;
      const msgs = this.ticketMessages || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].kind === 'close') return msgs[i].id === m.id;
      }
      return false;
    }

    // Owner side + closed + unanswered → ask on the latest close notice.
    showSolvedPrompt(m) {
      return this.isActiveCloseMessage(m) && this.ticketViewSource !== 'admin'
        && this.ticketDetail.solved == null;
    }

    solvedLabel() {
      return this.ticketDetail.solved ? 'Problem solved' : 'Problem not solved';
    }

    // Thumbs belong on support replies the owner didn't write themselves (an
    // admin's own ticket can contain their own support-side replies), and
    // only on the owner's side of the view.
    canRateMessage(m) {
      return this.ticketViewSource !== 'admin' && m.kind === 'message' && !!m.is_admin
        && m.user_id !== this.ticketDetail.user_id;
    }

    backFromTicket() {
      this.loadTicketUnreadCounts();
      // The list is the previous history entry (opened via openTicket, or
      // seeded behind a deep-linked ticket), so a real Back returns to it.
      history.back();
    }

    // Fetch + render a ticket conversation. Does NOT touch the URL — the router
    // owns that; this is also reused to refresh in place after reply/close.
    async viewTicket(ticketID, source) {
      // Stamp each open; a stale ticket fetch must not overwrite a newer
      // navigation (open A, then quickly open B or go elsewhere before A lands).
      const seq = ++this._ticketViewSeq;
      // Tell the server which side we're viewing as. It's only consulted for
      // an admin opening their OWN ticket (support view vs their customer
      // view); for everyone else — and when empty — the server decides from
      // role + ownership, resolving an admin's own ticket to the customer
      // side unless the URL claimed the support one.
      //
      // The side asked for rides the REQUEST only; the view follows the
      // server's answer below, never the click. It used to be written here
      // first, which flipped the support-side controls — the AI button — the
      // moment a system link was followed, a beat before the re-read landed:
      // words typed into the reply box in that beat were then replaced by the
      // support template, a script write the textarea's undo history never
      // sees, so ⌘Z brought back the template instead of the typing
      // (2026-09-02, the portal suite racing that beat under a 15-lane run).
      const src = source || this.ticketViewSource;
      try {
        const response = await this.apiFetch(this.apiUrl('ticket?id=' + encodeURIComponent(ticketID) + '&source=' + src));
        if (response.ok) {
          const data = await response.json();
          if (seq !== this._ticketViewSeq) return false;
          // Opening a different ticket: image previews and picked-but-unsent
          // reply files belong to the previous one.
          if (this._attachmentTicket !== data.id) {
            this.revokeAttachmentUrls();
            this._attachmentTicket = data.id;
            this._revokePicked(this.replyFiles);
            this.replyFiles = [];
          }
          this.ticketDetail = data;
          this.ticketMessages = data.messages || [];
          // Follow the server's role decision for display (bubbles, admin reply
          // template) instead of the click source, which goes stale on Back.
          this.ticketViewSource = data.is_support_view ? 'admin' : 'user';
          this.ticketReplyBody = this.defaultReplyBody();
          this.page = 'ticket-view';
          this.host()?._syncTicketRoute?.(ticketID);
          this.update();
          // Jump to the newest message: the chat scrolls internally in panel
          // mode; in document mode (small screens) the content column is the
          // scroller. Setting both is harmless — the inert one stays at 0.
          var container = this.querySelector('.ticket-messages');
          if (container) container.scrollTop = container.scrollHeight;
          var content = this.closest('.content');
          if (content) content.scrollTop = content.scrollHeight;
          this.loadAttachmentPreviews();
          this.loadTicketUnreadCounts();
          return true;
        }
      } catch (e) {}
      return false;
    }

    async sendTicketReply() {
      if (!this.ticketReplyBody && this.replyFiles.length === 0) return;
      if (this.ticketReplyLoading) return;
      this.ticketReplyLoading = true;
      this.update();
      try {
        // Attachments switch the post to multipart/form-data (see
        // submitNewTicket); text-only replies keep the JSON body.
        let options;
        if (this.replyFiles.length > 0) {
          const fd = new FormData();
          fd.append('ticket_id', this.ticketDetail.id);
          fd.append('body', this.ticketReplyBody);
          fd.append('source', this.ticketViewSource);
          for (const f of this.replyFiles) fd.append('files', f, f.name);
          options = { method: 'POST', body: fd };
        } else {
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket_id: this.ticketDetail.id, body: this.ticketReplyBody, source: this.ticketViewSource }),
          };
        }
        const response = await this.apiFetch(`${this.apiUrl('ticket/reply')}`, options);
        const data = await response.json();
        if (response.ok) {
          this.ticketReplyBody = '';
          this._revokePicked(this.replyFiles);
          this.replyFiles = [];
          await this.viewTicket(this.ticketDetail.id);
          this.loadTicketUnreadCounts();
        } else {
          this.showToast(data, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      } finally {
        this.ticketReplyLoading = false;
      }
    }

    // Ask the AI backend to draft a support reply from the ticket conversation.
    // Whatever is already typed goes along as context (a partial draft the AI
    // completes) and the suggestion replaces the reply box, ready to edit.
    // Files picked for this reply ride along too (multipart), so the draft can
    // reference what they show — they stay picked and are only uploaded for
    // real when the reply is sent.
    async suggestTicketReply() {
      if (this.ticketAiLoading) return;
      this.ticketAiLoading = true;
      this.update();
      try {
        let options;
        if (this.replyFiles.length > 0) {
          const fd = new FormData();
          fd.append('ticket_id', this.ticketDetail.id);
          fd.append('draft', this.ticketReplyBody);
          for (const f of this.replyFiles) fd.append('files', f, f.name);
          options = { method: 'POST', body: fd };
        } else {
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket_id: this.ticketDetail.id, draft: this.ticketReplyBody }),
          };
        }
        const response = await this.apiFetch(`${this.apiUrl('admin/ticket/ai-suggest')}`, options);
        const data = await response.json();
        if (response.ok) {
          this.writeReplyBody(data.suggestion || '');
        } else {
          this.showToast(data, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      } finally {
        this.ticketAiLoading = false;
        this.update();
      }
    }

    // Put text in the reply box as an edit the browser can take back.
    // Assigning the model instead (a re-render then writes textarea.value)
    // is invisible to the textarea's own undo history, so cmd/ctrl-Z could
    // not bring back the words the draft replaced. execCommand('insertText')
    // over the whole selection is ONE native undo step; it needs the textarea
    // focused, and the input event it fires is what lw-model listens to, so
    // the model follows by itself — on the undo too. The re-render below
    // leaves the value alone (leanweb only assigns when it differs), which is
    // what keeps the undo history from being thrown away again.
    writeReplyBody(text) {
      const box = this.querySelector('.ticket-reply textarea');
      if (box && box.value !== text) {
        box.focus();
        box.select();
        let written = false;
        try {
          // insertText with an empty string does nothing in some browsers,
          // so an empty draft is a deletion instead.
          written = text
            ? document.execCommand('insertText', false, text)
            : document.execCommand('delete');
        } catch (e) {}
        if (written) {
          this.ticketReplyBody = box.value;
          this.update();
          return;
        }
      }
      this.ticketReplyBody = text;
      this.update();
    }

    // Close flow. The support view keeps the plain confirm; the owner gets
    // the close dialog instead, which bundles the closing comment, the
    // "was your problem solved?" answer and the star rating into one step.
    // The stars pre-fill with the ticket's current rating, so closing again
    // after a reopen re-rates rather than starting blank.
    async closeTicket(ticketID) {
      if (this.ticketViewSource === 'admin') {
        const confirmed = await this.showConfirm({
          title: 'Close Ticket',
          message: 'Are you sure you want to close this ticket?',
          action: 'Close',
          danger: true,
        });
        if (!confirmed) return;
        await this.postCloseTicket(ticketID, { source: this.ticketViewSource });
        return;
      }
      this.closeComment = '';
      this.closeSolved = null;
      this.closeRating = this.ticketDetail.rating || 0;
      this.update();
      this.querySelector('.close-ticket-dialog').showModal();
    }

    closeCloseTicketDialog() {
      this.querySelector('.close-ticket-dialog').close();
    }

    // Tri-state toggles for the close dialog; clicking the active choice
    // clears it back to unanswered / unrated.
    setCloseSolved(value) {
      this.closeSolved = this.closeSolved === value ? null : value;
      this.update();
    }

    setCloseRating(stars) {
      this.closeRating = this.closeRating === stars ? 0 : stars;
      this.update();
    }

    async submitCloseTicket() {
      if (this.closeTicketLoading) return;
      this.closeTicketLoading = true;
      this.update();
      const ok = await this.postCloseTicket(this.ticketDetail.id, {
        comment: this.closeComment.trim(),
        solved: this.closeSolved,
        rating: this.closeRating,
        source: this.ticketViewSource,
      });
      this.closeTicketLoading = false;
      if (ok) this.closeCloseTicketDialog();
      this.update();
    }

    async postCloseTicket(ticketID, payload) {
      try {
        const response = await this.apiFetch(`${this.apiUrl('ticket/close?id=' + encodeURIComponent(ticketID))}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const msg = await response.json();
        if (!response.ok) {
          this.showToast(msg, 'danger');
          return false;
        }
        this.showToast(msg);
        await this.viewTicket(ticketID);
        return true;
      } catch (e) {
        this.showToast(e.message, 'danger');
        return false;
      }
    }

    // Reopen a closed ticket (owner or admin). Non-destructive, so no confirm.
    async reopenTicket(ticketID) {
      try {
        const response = await this.apiFetch(`${this.apiUrl('ticket/reopen?id=' + encodeURIComponent(ticketID))}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: this.ticketViewSource }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.viewTicket(ticketID);
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    }

    // The owner's answer to the "was your problem solved?" prompt on the
    // close notice. Updates in place — no full thread reload for one field.
    async answerSolved(solved) {
      try {
        const response = await this.apiFetch(`${this.apiUrl('ticket/solved')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket_id: this.ticketDetail.id, solved }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.ticketDetail.solved = solved ? 1 : 0;
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
      this.update();
    }

    // Thumbs on a support reply; clicking the active thumb clears it.
    async rateMessage(m, value) {
      const rating = m.rating === value ? 0 : value;
      try {
        const response = await this.apiFetch(`${this.apiUrl('ticket/message/rate')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_id: m.id, rating }),
        });
        const msg = await response.json();
        if (response.ok) {
          m.rating = rating || null;
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
      this.update();
    }
}

Object.assign(WebTicketView.prototype, attachments);

customElements.define('web-ticket-view', WebTicketView);
